<template>
    <div class="tw:rounded-lg tw:border tw:border-slate-200 tw:bg-slate-50/70 tw:p-3" data-cy="resumen-ventas-desglose">
        <div class="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:pb-2 tw:mb-2 tw:border-b tw:border-slate-200">
            <h4 class="tw:flex tw:items-center tw:gap-2 tw:m-0 tw:text-sm tw:font-semibold tw:text-slate-700">
                <i class="fas fa-layer-group tw:text-blue-600"></i>Desglose por zonas / precios
            </h4>
            <span v-if="!props.loading && !props.error" class="tw:text-xs tw:font-medium tw:text-slate-500" data-cy="resumen-ventas-zonas-activas">
                {{ props.desglose.length }} {{ props.desglose.length === 1 ? 'zona activa' : 'zonas activas' }}
            </span>
        </div>

        <p v-if="props.error" class="tw:flex tw:flex-col tw:items-start tw:gap-2 tw:m-0 tw:text-sm tw:text-red-600">
            No se ha podido cargar el desglose de la sesión.
            <Button variant="border-error" icon="fas fa-redo" @click="$emit('reintentar')">Reintentar</Button>
        </p>

        <BaseTable
            v-else
            naked
            :columns="columnas"
            :data="props.desglose"
            :skeleton="props.loading"
            :skeleton-rows="2"
            row-key="nombre_zona"
            empty-text="No hay ventas en esta sesión."
            data-cy="resumen-ventas-desglose-tabla"
        >
            <template #cell-zona="{ row }">
                <span class="tw:flex tw:items-center tw:gap-2 tw:font-medium tw:text-slate-700">
                    <span class="tw:h-2 tw:w-2 tw:shrink-0 tw:rounded-full tw:bg-blue-600"></span>
                    {{ row.nombre_zona }}
                </span>
            </template>

            <template #cell-taquilla="{ row }">
                {{ celdaVenta(row.venta_taquilla) }}
            </template>

            <template #cell-online="{ row }">
                {{ celdaVenta(row.venta_online) }}
            </template>

            <template #cell-invitaciones="{ row }">
                {{ celdaVenta(row.venta_invitaciones) }}
            </template>

            <template #cell-total_entradas="{ row }">
                <span class="tw:font-semibold">{{ celdaVenta(row.total_entradas) }}</span>
            </template>

            <template #cell-total_precio="{ row }">
                <span class="tw:font-bold tw:text-blue-700">{{ importe(row.total_precio) }}</span>
            </template>
        </BaseTable>
    </div>
</template>

<script setup>
    import appFilters from '@/filters';

    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue';
    import Button from '@/modules/admin/ui/Button.vue';

    const props = defineProps({
        /** Filas del endpoint de desglose de la sesión. */
        desglose: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: [Object, Error, null],
            default: null
        }
    });

    defineEmits(['reintentar']);

    const importe = appFilters.formatear_precio;

    /**
     * Nombres de columna. El backend entrega ya un valor por canal
     * (`venta_taquilla`, `venta_online`, `venta_invitaciones`,
     * `total_entradas`) que el legacy imprimía tal cual, así que aquí se
     * respeta: no se reinterpreta ni se reformatea.
     */
    const columnas = [
        { key: 'zona', label: 'Nombre zona', field: 'nombre_zona', class: 'tw:w-1/3' },
        { key: 'taquilla', label: 'Venta taquilla', field: 'venta_taquilla', centered: true, class: 'tw:w-32' },
        { key: 'online', label: 'Venta online', field: 'venta_online', centered: true, class: 'tw:w-32' },
        { key: 'invitaciones', label: 'Invitaciones', field: 'venta_invitaciones', centered: true, class: 'tw:w-28' },
        { key: 'total_entradas', label: 'Total entradas', field: 'total_entradas', centered: true, class: 'tw:w-28' },
        { key: 'total_precio', label: 'Recaudación total', field: 'total_precio', centered: true, class: 'tw:w-32' }
    ];

    /** Celda vacía cuando el backend no manda valor, en vez de "undefined". */
    const celdaVenta = valor => (valor === null || valor === undefined || valor === '' ? '—' : valor);
</script>
